import React, { useState, useEffect } from 'react';
import { FiUser, FiBriefcase, FiPercent, FiSave, FiArrowLeft, FiEdit, FiMail } from 'react-icons/fi';
import { FaRupeeSign } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { database, ref, set, get } from '../firebase';

const EditProfile = () => {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    mobile: '',
    plan: '',
    business: '',
    gst: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Load user data from localStorage
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (!currentUser || !currentUser.mobile) {
      navigate('/login');
      return;
    }

    setFormData({
      name: currentUser.name || '',
      surname: currentUser.surname || '',
      email: currentUser.email || '',
      mobile: currentUser.mobile || '',
      plan: currentUser.plan || '',
      business: currentUser.business || '',
      gst: currentUser.gst || '',
    });
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.gst && (isNaN(formData.gst) || formData.gst < 0 || formData.gst > 100)) {
      newErrors.gst = 'GST must be a number between 0 and 100';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const userRef = ref(database, `videoplayer/Users/${formData.mobile}`);
      const updatedUser = {
        ...formData,
        phoneNumber: formData.mobile,
        termsAccepted: true, // Preserve existing termsAccepted value
      };

      await set(userRef, {
        ...updatedUser,
        // Preserve other fields not editable in this form
        password: (await get(userRef)).val()?.password || '',
        profilePictureUrl: (await get(userRef)).val()?.profilePictureUrl || null,
        status: (await get(userRef)).val()?.status || 'pending',
        timestamp: (await get(userRef)).val()?.timestamp || new Date().toISOString(),
      });

      // Update localStorage
      localStorage.setItem('currentUser', JSON.stringify(updatedUser));
      alert('Profile updated successfully!');
      navigate('/profile');
    } catch (error) {
      console.error('Firebase Error:', error);
      setErrors({ submit: 'An error occurred while updating your profile. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-200 flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-6 text-center flex items-center justify-center">
          <FiEdit className="mr-3 text-blue-600 text-3xl" /> Edit Profile
        </h2>
        {Object.keys(errors).length > 0 && (
          <div
            className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 text-sm border border-red-200"
            aria-live="polite"
          >
            {Object.values(errors).map((error, idx) => (
              <p key={idx}>{error}</p>
            ))}
          </div>
        )}
        <div className="space-y-5">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FiUser className="mr-2 text-blue-600" /> Name <span className="text-red-500 ml-1">*</span>
            </label>
            <input
              id="name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your name"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "name-error" : undefined}
            />
            {errors.name && <p id="name-error" className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor="surname" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FiUser className="mr-2 text-blue-600" /> Surname
            </label>
            <input
              id="surname"
              type="text"
              name="surname"
              value={formData.surname}
              onChange={handleChange}
              placeholder="Enter your surname"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              aria-invalid={!!errors.surname}
              aria-describedby={errors.surname ? "surname-error" : undefined}
            />
            {errors.surname && <p id="surname-error" className="text-red-500 text-xs mt-1">{errors.surname}</p>}
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FiMail className="mr-2 text-blue-600" /> Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "email-error" : undefined}
            />
            {errors.email && <p id="email-error" className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>
          <div>
            <label htmlFor="plan" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FaRupeeSign  className="mr-2 text-blue-600" /> Plan
            </label>
            <select
              id="plan"
              name="plan"
              value={formData.plan}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select a plan</option>
              <option value="yearly">Yearly Plan</option>
              <option value="unlimited">Unlimited Plan</option>
            </select>
          </div>
          <div>
            <label htmlFor="business" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FiBriefcase className="mr-2 text-blue-600" /> Business
            </label>
            <select
              id="business"
              name="business"
              value={formData.business}
              onChange={handleChange}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">Select business status</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div>
            <label htmlFor="gst" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FiPercent className="mr-2 text-blue-600" /> GST (%)
            </label>
            <input
              id="gst"
              type="number"
              name="gst"
              value={formData.gst}
              onChange={handleChange}
              placeholder="Enter GST percentage"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              min="0"
              max="100"
              step="0.1"
              aria-invalid={!!errors.gst}
              aria-describedby={errors.gst ? "gst-error" : undefined}
            />
            {errors.gst && <p id="gst-error" className="text-red-500 text-xs mt-1">{errors.gst}</p>}
          </div>
          <div className="flex space-x-4 w-full">
            <button
              onClick={handleCancel}
              className="w-1/2 bg-gray-600 text-white p-3 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-300 flex items-center justify-center"
            >
              <FiArrowLeft className="mr-2" /> Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-1/2 bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 flex items-center justify-center disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                <>
                  <FiSave className="mr-2" /> Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;